import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import * as yup from "yup";

const Form = ({
  title,
  fields,
  onSubmit,
  buttonText,
  defaultValues = {},
  validationSchema,
}) => {
  const [formValues, setFormValues] = useState(defaultValues);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateField = async (fieldName, value) => {
    try {
      if (validationSchema) {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        setErrors((prev) => ({ ...prev, [fieldName]: "" }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, [fieldName]: error.message }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (validationSchema) {
        await validationSchema.validate(formValues, { abortEarly: false });
        setErrors({});
      }

      setIsLoading(true);
      await onSubmit(formValues);
    } catch (validationError) {
      if (validationError.inner) {
        const newErrors = {};
        validationError.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", sm: 400, md: 450 },
        margin: "0 auto",
        padding: { xs: 2, sm: 3 },
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: { xs: 1, sm: 2 },
        boxShadow: { xs: 1, sm: 2 },
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Typography 
        variant={isMobile ? "h6" : "h5"} 
        component="h2" 
        textAlign="center" 
        mb={2}
        sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
      >
        {title}
      </Typography>
      
      {fields.map((field) => (
        <TextField
          key={field.name}
          label={field.label}
          name={field.name}
          type={field.type || "text"}
          value={formValues[field.name] || ""}
          onChange={handleChange}
          fullWidth
          margin="normal"
          variant="outlined"
          error={Boolean(errors[field.name])}
          helperText={errors[field.name]}
          size={isMobile ? "small" : "medium"}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
            },
          }}
        />
      ))}
      
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={isLoading}
        sx={{ 
          mt: 2,
          py: { xs: 1, sm: 1.5 },
          fontSize: { xs: '0.875rem', sm: '1rem' },
        }}
      >
        {isLoading ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          buttonText
        )}
      </Button>
    </Box>
  );
};

export default Form;
